import { useEffect, useState, useCallback } from "react";
import { Plus, Pencil, Trash2, Power, Newspaper } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { PageTitle, Panel } from "./AdminShell";
import { Loader } from "@/components/common";
import Modal, { TextField } from "@/components/Modal";
import RichEditor from "@/components/RichEditor";
import { ImageUploader } from "@/components/common";
import { formatDate } from "@/lib/format";
import { toast } from "sonner";

export default function AdminBlog() {
  const [posts, setPosts] = useState(null);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => api.get("/admin/blog").then((r) => setPosts(r.data)), []);
  useEffect(() => { load(); }, [load]);
  if (!posts) return <Loader />;

  const togglePub = async (p) => {
    try { await api.put(`/admin/blog/${p.id}`, { ...p, published: !p.published }); toast.success(p.published ? "Dépublié" : "Publié"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };
  const del = async (p) => {
    if (!window.confirm("Supprimer cet article ?")) return;
    try { await api.delete(`/admin/blog/${p.id}`); toast.success("Article supprimé"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div>
      <PageTitle action={<button onClick={() => setEdit({ published: false })} className="bg-[#C85A32] text-white font-bold text-sm px-4 py-2 rounded-lg flex items-center gap-1" data-testid="add-blog-btn"><Plus className="w-4 h-4" /> Nouvel article</button>}>Blog</PageTitle>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {posts.length === 0 && <p className="text-sm text-slate-400">Aucun article.</p>}
        {posts.map((p) => (
          <Panel key={p.id} className="overflow-hidden" data-testid={`blog-card-${p.id}`}>
            {p.image && <img src={p.image} alt="" className="w-full h-32 object-cover" />}
            <div className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${p.published ? "bg-green-100 text-green-700" : "bg-slate-200 text-slate-500"}`}>{p.published ? "Publié" : "Brouillon"}</span>
                <span className="text-xs text-slate-400 ml-auto">{formatDate(p.created_at)}</span>
              </div>
              <p className="font-semibold text-sm">{p.title}</p>
              <p className="text-xs text-slate-500 mt-1 line-clamp-2">{p.excerpt}</p>
              <div className="flex gap-2 mt-3">
                <button onClick={() => setEdit(p)} className="flex-1 border border-slate-200 text-slate-700 text-xs font-bold py-1.5 rounded-lg inline-flex items-center justify-center gap-1" data-testid={`blog-edit-${p.id}`}><Pencil className="w-3.5 h-3.5" /> Modifier</button>
                <button onClick={() => togglePub(p)} className="flex-1 border border-slate-200 text-slate-700 text-xs font-bold py-1.5 rounded-lg inline-flex items-center justify-center gap-1" data-testid={`blog-pub-${p.id}`}><Power className="w-3.5 h-3.5" /> {p.published ? "Dépublier" : "Publier"}</button>
                <button onClick={() => del(p)} className="border border-red-200 text-red-600 text-xs font-bold py-1.5 px-2 rounded-lg" data-testid={`blog-del-${p.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          </Panel>
        ))}
      </div>
      {edit && <BlogForm post={edit} onClose={() => setEdit(null)} onDone={load} />}
    </div>
  );
}

function BlogForm({ post, onClose, onDone }) {
  const [f, setF] = useState({ title: "", slug: "", excerpt: "", content: "", image: "", published: false, ...post });
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    try {
      if (post.id) await api.put(`/admin/blog/${post.id}`, f);
      else await api.post("/admin/blog", f);
      toast.success("Article enregistré"); onDone(); onClose();
    } catch (err) { toast.error(apiError(err)); }
  };
  return (
    <Modal title={post.id ? "Modifier l'article" : "Nouvel article"} onClose={onClose} testid="blog-form" size="lg">
      <form onSubmit={submit} className="space-y-3">
        <TextField label="Titre" value={f.title} onChange={set("title")} required testid="bf-title" />
        <TextField label="Slug (facultatif)" value={f.slug} onChange={set("slug")} required={false} testid="bf-slug" placeholder="genere-depuis-le-titre" />
        <TextField label="Extrait" value={f.excerpt} onChange={set("excerpt")} required={false} testid="bf-excerpt" />
        <div><span className="text-sm font-semibold text-slate-700 block mb-1.5">Image de couverture</span>
          <ImageUploader value={f.image ? [f.image] : []} onChange={(arr) => set("image")(arr[0] || "")} multiple={false} testid="bf-image" />
        </div>
        <div><span className="text-sm font-semibold text-slate-700 block mb-1.5">Contenu</span>
          <RichEditor value={f.content} onChange={set("content")} testid="bf-content" />
        </div>
        <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
          <input type="checkbox" checked={f.published} onChange={(e) => set("published")(e.target.checked)} data-testid="bf-published" /> Publier immédiatement
        </label>
        <button className="w-full bg-[#C85A32] text-white font-bold py-3 rounded-lg" data-testid="bf-submit">Enregistrer</button>
      </form>
    </Modal>
  );
}
